import { CSSResult, html, PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';

import List from '../list';
import type Option from '../option';
import { TAG_NAME as OPTION_TAGNAME } from '../option/option.constants';
import { ROLE } from '../../utils/roles';
import { CaptureDestroyEventForChildElement } from '../../utils/mixins/lifecycle/CaptureDestroyEventForChildElement';
import { ListNavigationMixin } from '../../utils/mixins/ListNavigationMixin';
import { Component } from '../../models';
import { LifeCycleModifiedEvent } from '../../utils/mixins/lifecycle/LifeCycleModifiedEvent';
import { ElementStore } from '../../utils/controllers/ElementStore';

import styles from './listbox.styles';

/**
 * listbox component presents a list of options and allows a user to select one or more of them.
 *
 * When `multiple` is enabled, clicking/pressing Enter/Space on an option toggles its selection
 * instead of replacing the current selection.
 *
 * Notes:
 * - This is a standalone listbox component. Select has its own mdc-selectlistbox component.
 * - this component has name and value attributes and also emits change event,
 *   but it is not a form control (yet).
 *
 * @dependency mdc-list
 * @dependency mdc-icon
 * @dependency mdc-text
 * @dependency mdc-option
 * @dependency mdc-optgroup
 *
 * @tagname mdc-listbox
 *
 * @cssproperty --mdc-listbox-max-height - max height of the listbox
 *
 * @slot default - This is a default/unnamed slot, where options and optgroups are placed
 *
 * @csspart container - The container of the listbox
 *
 * @event change - (React: onChange) This event is emitted when the selected item changed
 */
class ListBox extends ListNavigationMixin(CaptureDestroyEventForChildElement(Component)) {
  /**
   * According to WCAG this is the expected behavior for listbox
   * https://www.w3.org/WAI/ARIA/apg/practices/listbox
   * @internal
   */
  protected override loop: 'true' | 'false' = 'false';

  /**
   * The name attribute is used to identify the listbox
   */
  @property({ type: String, reflect: true }) name?: undefined | string = undefined;

  /**
   * The value attribute is used to represent the last selected option's value in the listbox.
   */
  @property({ type: String, reflect: true }) value?: undefined | string = undefined;

  /**
   * When true, multiple options can be selected.
   * @default false
   */
  @property({ type: Boolean, reflect: true }) multiple = false;

  /** @internal */
  @state() selectedOption?: Option | null;

  /** @internal */
  private itemsStore = new ElementStore<Option>(this, {
    isValidItem: this.isValidItem,
  });

  constructor() {
    super();
    this.addEventListener('click', this.handleClick);
    this.addEventListener('modified', this.handleModifiedEvent);
    this.addEventListener('destroyed', this.handleDestroyEvent);
  }

  override connectedCallback() {
    super.connectedCallback();
    this.role = ROLE.LISTBOX;
  }

  /** @internal */
  private handleModifiedEvent(event: LifeCycleModifiedEvent) {
    const item = event.target as Option;

    switch (event.detail.change) {
      case 'enabled':
        this.itemsStore.add(item);
        break;
      case 'disabled':
        this.itemsStore.delete(item);
        break;
      case 'selected':
        if (this.multiple) {
          this.syncValueToFirstSelected();
        } else {
          this.setSelectedOption(item, false, false);
        }
        break;
      case 'unselected':
        if (this.multiple) {
          this.syncValueToFirstSelected();
        } else {
          this.handleNoSelection();
        }
        break;
      default:
        break;
    }
  }

  /**
   * If nothing is selected anymore, clear the value
   * @internal
   */
  private handleNoSelection() {
    const selectedOption = this.getFirstSelectedOption();
    if (!selectedOption) {
      this.selectedOption = undefined;
      this.value = undefined;
    }
  }

  /** @internal */
  private handleDestroyEvent = () => {
    this.handleNoSelection();
  };

  /** @internal */
  protected get navItems(): HTMLElement[] {
    return this.itemsStore.items;
  }

  /** @internal */
  private isValidItem(item: Element): boolean {
    return item.matches(`${OPTION_TAGNAME}:not([disabled])`);
  }

  /** @internal */
  private handleClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const option = target.closest(OPTION_TAGNAME) as Option | null;
    if (!option || !this.isValidItem(option)) return;

    if (this.multiple) {
      this.toggleOptionSelection(option);
    } else {
      this.setSelectedOption(option);
    }
  }

  /**
   * Toggles the selection state of an option (for multiselect mode).
   * @internal
   */
  private toggleOptionSelection(option: Option): void {
    if (option.disabled || option.softDisabled) return;

    const isCurrentlySelected = option.hasAttribute('selected');
    option.toggleAttribute('selected', !isCurrentlySelected);

    this.syncValueToFirstSelected();
    this.fireEvents();
  }

  /** @internal */
  private getFirstSelectedOption() {
    return this.itemsStore.items.find(el => el.matches('[selected]'));
  }

  /**
   * Syncs value and selectedOption to reflect the first selected option (when multiple)
   * @internal
   */
  private syncValueToFirstSelected(): void {
    const firstSelected = this.getFirstSelectedOption();
    this.value = firstSelected?.value;
    this.selectedOption = firstSelected ?? null;
  }

  /** @internal */
  private getSelectedValues(): string[] {
    return this.itemsStore.items.reduce<string[]>((acc, option) => {
      const { value } = option;
      if (option.hasAttribute('selected') && value !== undefined) {
        acc.push(value);
      }
      return acc;
    }, []);
  }

  /**
   * Override to focus the first selected option per W3C APG.
   * This applies to both single-select and multi-select listboxes.
   * @internal
   */
  protected override setInitialFocus(): void {
    const firstSelected = this.getFirstSelectedOption();
    if (firstSelected) {
      const index = this.itemsStore.items.indexOf(firstSelected);
      if (index !== -1) {
        this.resetTabIndexAndSetFocus(index, undefined, false);
        return;
      }
    }
    super.setInitialFocus();
  }

  /**
   * Handles the updated lifecycle event.
   *
   * @param changedProperties - The properties that have changed since the last update.
   */
  protected override updated(changedProperties: PropertyValues): void {
    if (changedProperties.has('multiple')) {
      if (this.multiple) {
        this.setAttribute('aria-multiselectable', 'true');
      } else {
        this.removeAttribute('aria-multiselectable');
      }
    }

    if (changedProperties.has('value') && !this.multiple) {
      const newSelectedOption = this.itemsStore.items.find(option => option.value === this.value);
      if (newSelectedOption) {
        this.setSelectedOption(newSelectedOption, false);
      } else {
        this.setSelectedOption(null, false);
      }
    }
  }

  /**
   * When an option is selected, this method updates local and options' DOM state and fires the change event.
   *
   * @param option - The option element in DOM which gets selected.
   * @param fireEvent - A boolean flag to indicate whether to fire the change event or not.
   * @param updateOptions - Whether update the other options or not
   */
  private setSelectedOption(option?: Option | null, fireEvent = true, updateOptions = true): void {
    if (option?.disabled || option?.softDisabled) return;
    if (updateOptions) {
      this.updateSelectedInChildOptions(option);
    }

    // set the selected option in the component state
    this.selectedOption = option;

    this.value = option?.value;

    if (fireEvent) {
      this.fireEvents();
    }
  }

  /**
   * Update all options selection state in the DDM.
   *
   * @param selectedOption - The option which gets selected
   */
  private updateSelectedInChildOptions(selectedOption?: Option | null): void {
    this.itemsStore.items.forEach(option => option.removeAttribute('selected'));
    selectedOption?.toggleAttribute('selected', true);
  }

  /**
   * Dispatch change event when selection changes.
   */
  private fireEvents(): void {
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: {
          value: this.value,
          selectedValues: this.getSelectedValues(),
        },
        composed: true,
        bubbles: true,
      }),
    );
  }

  override render() {
    return html`<slot part="container"></slot>`;
  }

  public static override styles: Array<CSSResult> = [...List.styles, ...styles];
}

export default ListBox;
